// utils/highlightsApi.ts
// ハイライト（抜粋・メモ）API（rl_book_id ベース、api.readlinker.com/highlights）
// 全操作で認証（Cognito ID token）が必要。
// レスポンスは { success: true, data: {...} } / { success: false, error: { message } }。

import axios from 'axios';
import { NEW_API } from '../config/api';
import { HighlightVisibility } from '../types/rlTypes';

const HIGHLIGHTS_URL = NEW_API.HIGHLIGHTS;

function authHeaders(idToken: string) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
  };
}

/** ハイライト付き書籍一覧を取得（件数 + 書誌情報付き） */
export async function fetchHighlightBooks(idToken: string) {
  const res = await axios.get(HIGHLIGHTS_URL, { headers: authHeaders(idToken) });
  return res.data; // { success, data: { items: HighlightBookItem[] } }
}

/**
 * ハイライトを作らずに本だけを「抜粋・メモする本」として登録する（rl_highlight_books upsert）。
 * 既に登録済みでもエラーにはならない（バックエンド側でupsertのため冪等）
 */
export async function registerHighlightBook(rlBookId: number, idToken: string) {
  const res = await axios.post(
    HIGHLIGHTS_URL,
    { operation_type: 'register_book', rl_book_id: rlBookId },
    { headers: authHeaders(idToken) },
  );
  return res.data; // { success, data: { user_sub, rl_book_id } }
}

/** 指定書籍のハイライト一覧を取得 */
export async function fetchBookHighlights(rlBookId: number, idToken: string) {
  const res = await axios.get(HIGHLIGHTS_URL, {
    headers: authHeaders(idToken),
    params: { rl_book_id: rlBookId },
  });
  return res.data; // { success, data: { items: Highlight[] } }
}

/** ハイライトを作成（Web は source_type='manual' のみ。text 必須） */
export async function createHighlight(
  params: {
    rlBookId: number;
    text: string;
    pageNumber?: number | null;
    visibility?: HighlightVisibility;
  },
  idToken: string,
) {
  const body: Record<string, unknown> = {
    operation_type: 'create_highlight',
    rl_book_id: params.rlBookId,
    source_type: 'manual',
    text: params.text,
  };
  if (params.pageNumber !== undefined && params.pageNumber !== null) {
    body.page_number = params.pageNumber;
  }
  if (params.visibility) {
    body.visibility = params.visibility;
  }

  const res = await axios.post(HIGHLIGHTS_URL, body, { headers: authHeaders(idToken) });
  return res.data; // { success, data: Highlight }
}

/** ハイライトを更新（text / page_number / visibility の部分更新） */
export async function updateHighlight(
  highlightId: string,
  updates: {
    text?: string;
    page_number?: number | null;
    visibility?: HighlightVisibility;
  },
  idToken: string,
) {
  const res = await axios.post(
    HIGHLIGHTS_URL,
    { operation_type: 'update_highlight', highlight_id: highlightId, ...updates },
    { headers: authHeaders(idToken) },
  );
  return res.data; // { success, data: Highlight }
}

/** ハイライトを削除 */
export async function deleteHighlight(highlightId: string, idToken: string) {
  const res = await axios.post(
    HIGHLIGHTS_URL,
    { operation_type: 'delete_highlight', highlight_id: highlightId },
    { headers: authHeaders(idToken) },
  );
  return res.data; // { success, data: { deleted_highlight_id, rl_book_id, remaining_highlights } }
}

/** 公開ハイライト付き書籍一覧を取得（認証不要、プロフィール画面用） */
export async function fetchPublicHighlightBooks(targetUserSub: string) {
  const res = await axios.get(HIGHLIGHTS_URL, {
    params: { public_user_sub: targetUserSub },
  });
  return res.data; // { success, data: { items: HighlightBookItem[] } }
}

/** 指定ユーザーの指定書籍における公開ハイライト一覧を取得（認証不要） */
export async function fetchPublicBookHighlights(targetUserSub: string, rlBookId: number) {
  const res = await axios.get(HIGHLIGHTS_URL, {
    params: { public_user_sub: targetUserSub, rl_book_id: rlBookId },
  });
  return res.data; // { success, data: { items: Highlight[] } }
}
